import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Grid, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Chip, CircularProgress, Snackbar, Alert,
  Accordion, AccordionSummary, AccordionDetails, LinearProgress,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import TuneIcon from '@mui/icons-material/Tune';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { format, startOfWeek, addWeeks } from 'date-fns';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('authToken')}`
});

// ─── Constants & Hardcoded Fallbacks ─────────────────────────────────────────

const DAYS    = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DOW_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

const ROLES = [
  'AM Pickers', 'AM Lead', 'Receivers',
  'PM Pickers', 'PM Lead', 'PM Replenisher',
  'Drivers', 'ATL Helpers', 'Supervisor'
];

const FALLBACK_DEMAND = {
  0: { am: 685,  pm: 2427 },
  1: { am: 655,  pm: 1428 },
  2: { am: 931,  pm: 1948 },
  3: { am: 0,    pm: 1904 },
  4: { am: 956,  pm: 2446 },
  5: { am: 273,  pm: 1657 },
  6: { am: 555,  pm: 1272 },
};

const FALLBACK_SCHEDULE = {
  0: { 'AM Pickers': 4, 'AM Lead': 1, 'Receivers': 0, 'PM Pickers': 10, 'PM Lead': 1, 'PM Replenisher': 1, 'Drivers': 3, 'ATL Helpers': 2, 'Supervisor': 1 },
  1: { 'AM Pickers': 4, 'AM Lead': 1, 'Receivers': 3, 'PM Pickers': 8,  'PM Lead': 1, 'PM Replenisher': 1, 'Drivers': 3, 'ATL Helpers': 2, 'Supervisor': 1 },
  2: { 'AM Pickers': 5, 'AM Lead': 1, 'Receivers': 3, 'PM Pickers': 9,  'PM Lead': 1, 'PM Replenisher': 1, 'Drivers': 3, 'ATL Helpers': 2, 'Supervisor': 1 },
  3: { 'AM Pickers': 0, 'AM Lead': 0, 'Receivers': 2, 'PM Pickers': 9,  'PM Lead': 1, 'PM Replenisher': 1, 'Drivers': 3, 'ATL Helpers': 2, 'Supervisor': 1 },
  4: { 'AM Pickers': 5, 'AM Lead': 1, 'Receivers': 3, 'PM Pickers': 10, 'PM Lead': 1, 'PM Replenisher': 1, 'Drivers': 3, 'ATL Helpers': 2, 'Supervisor': 1 },
  5: { 'AM Pickers': 2, 'AM Lead': 1, 'Receivers': 2, 'PM Pickers': 8,  'PM Lead': 1, 'PM Replenisher': 1, 'Drivers': 3, 'ATL Helpers': 2, 'Supervisor': 1 },
  6: { 'AM Pickers': 4, 'AM Lead': 1, 'Receivers': 0, 'PM Pickers': 7,  'PM Lead': 1, 'PM Replenisher': 0, 'Drivers': 3, 'ATL Helpers': 2, 'Supervisor': 1 },
};

const FALLBACK_RECV_REQ = { 0: 0, 1: 3, 2: 3, 3: 3, 4: 3, 5: 2, 6: 0 };

const FALLBACK_TRANSPORT = [
  { platform: 'ATL A&B',     shift: 'AM', dispatch: '06:00', carrier: 'Company Truck', days: [1,2,3,4,5] },
  { platform: 'ATL E/T/ATR', shift: 'PM', dispatch: '14:00', carrier: 'Company Truck', days: [0,1,2,3,4,5,6] },
  { platform: 'ASH',         shift: 'PM', dispatch: '15:00', carrier: 'FedEx',          days: [1,3,5] },
  { platform: 'BNA',         shift: 'PM', dispatch: '15:00', carrier: 'FedEx',          days: [1,3,5] },
  { platform: 'CAE',         shift: 'PM', dispatch: '14:30', carrier: 'FedEx',          days: [2,4] },
  { platform: 'GSO',         shift: 'PM', dispatch: '14:30', carrier: 'FedEx',          days: [2,4] },
  { platform: 'MEM',         shift: 'PM', dispatch: '13:00', carrier: 'FedEx',          days: [1,2,3,4,5] },
  { platform: 'PNS',         shift: 'PM', dispatch: '15:00', carrier: 'FedEx',          days: [2,5] },
  { platform: 'RDU',         shift: 'PM', dispatch: '14:00', carrier: 'FedEx',          days: [1,3,5] },
  { platform: 'SAV',         shift: 'PM', dispatch: '15:30', carrier: 'FedEx',          days: [2,4] },
  { platform: 'TYS',         shift: 'PM', dispatch: '14:00', carrier: 'FedEx',          days: [1,4] },
];

const FALLBACK_CAT_LPH = [
  { category: 'Beverages',   lph: 45 },
  { category: 'Candy',       lph: 62 },
  { category: 'Snacks',      lph: 55 },
  { category: 'HBA',         lph: 40 },
  { category: 'Sundry',      lph: 48 },
  { category: 'Tobacco',     lph: 70 },
  { category: 'Electronics', lph: 35 },
  { category: 'Gifts',       lph: 38 },
  { category: 'Apparel',     lph: 32 },
  { category: 'Food',        lph: 50 },
];

// ─── Module-level data fetchers (no React deps) ───────────────────────────────

async function fetchPulseDataInternal() {
  const [demandRes, assocRes, catRes] = await Promise.allSettled([
    fetch(`${API_BASE}/api/labor-planner/demand-averages`, { headers: getAuthHeaders() }),
    fetch(`${API_BASE}/api/labor-planner/associate-lph`,   { headers: getAuthHeaders() }),
    fetch(`${API_BASE}/api/labor-planner/category-lph`,    { headers: getAuthHeaders() }),
  ]);

  const result = {};

  if (demandRes.status === 'fulfilled' && demandRes.value.ok) {
    const data = await demandRes.value.json();
    const pulseDemand = {};
    DOW_NAMES.forEach((name, di) => {
      const shifts = data.shiftLines?.[name];
      if (shifts) pulseDemand[di] = { am: Math.round(shifts.AM || 0), pm: Math.round(shifts.PM || 0) };
    });
    if (Object.keys(pulseDemand).length > 0) {
      result.demand   = pulseDemand;
      result.dataRange = data.dataRange;
    }
  }

  if (assocRes.status === 'fulfilled' && assocRes.value.ok) {
    const data = await assocRes.value.json();
    result.blendedLph  = data.blendedLph;
    result.associates  = data.associates || [];
    result.assocPeriod = data.period;
  }

  if (catRes.status === 'fulfilled' && catRes.value.ok) {
    const data = await catRes.value.json();
    if (data.categories && Object.keys(data.categories).length > 0) {
      result.catLPH    = Object.entries(data.categories).map(([category, lph]) => ({ category, lph }));
      result.catPeriod = data.period;
    }
  }

  return Object.keys(result).length > 0 ? result : null;
}

async function fetchSavedConfig() {
  const res = await fetch(`${API_BASE}/api/labor-planner/config`, { headers: getAuthHeaders() });
  if (res.ok) return await res.json(); // null if nothing saved
  return null;
}

// ─── Pure helpers ─────────────────────────────────────────────────────────────

const InfoTip = ({ text }) => (
  <Tooltip title={text} placement="right" arrow>
    <InfoOutlinedIcon sx={{ fontSize: 13, ml: 0.5, color: 'text.disabled', cursor: 'help', verticalAlign: 'middle' }} />
  </Tooltip>
);

const calcRequired = ({ dayIndex, demand, blendedLPH, productiveHours, calloutBuffer, recvReq, transport }) => {
  const amLines = Number(demand[dayIndex]?.am) || 0;
  const pmLines = Number(demand[dayIndex]?.pm) || 0;
  const buf = 1 + (Number(calloutBuffer) || 0) / 100;
  const lph = Number(blendedLPH) || 1;
  const hrs = Number(productiveHours) || 1;

  const amPickers = amLines > 0 ? Math.ceil(Math.ceil(amLines / (lph * hrs)) * buf) : 0;
  const pmPickers = pmLines > 0 ? Math.ceil(Math.ceil(pmLines / (lph * hrs)) * buf) : 0;

  const active    = (transport || []).filter(t => (t.days || []).includes(dayIndex));
  const atlActive = active.filter(t => t.platform.startsWith('ATL'));
  const outActive = active.filter(t => !t.platform.startsWith('ATL'));

  return {
    'AM Pickers':     amPickers,
    'AM Lead':        amPickers > 0 ? 1 : 0,
    'Receivers':      Number(recvReq[dayIndex]) || 0,
    'PM Pickers':     pmPickers,
    'PM Lead':        pmPickers > 0 ? 1 : 0,
    'PM Replenisher': pmLines >= 1500 ? 1 : 0,
    'Drivers':        Math.max(outActive.length, 2),
    'ATL Helpers':    atlActive.length > 0 ? 2 : 0,
    'Supervisor':     1,
  };
};

const healthScore = (scheduled, required) => {
  const ok = ROLES.filter(r => (scheduled[r] || 0) >= (required[r] || 0)).length;
  return Math.round((ok / ROLES.length) * 100);
};
const scoreColor  = (s) => s >= 80 ? '#2e7d32' : s >= 60 ? '#f57c00' : '#c62828';
const variantChip = (v) =>
  v > 0 ? { label: `+${v}`, bgcolor: '#e8f5e9', color: '#2e7d32' } :
  v === 0 ? { label: '0',  bgcolor: '#fff8e1', color: '#f57c00' } :
            { label: String(v), bgcolor: '#ffebee', color: '#c62828' };
const fmtDate = (d) => d ? format(new Date(d), 'MMM d, yyyy') : '—';

// ─── Main Component ───────────────────────────────────────────────────────────

export default function LaborPlanner() {
  const todayDow = new Date().getDay();

  const [weekStart,   setWeekStart]   = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [selectedDay, setSelectedDay] = useState(todayDow);
  const [inputsOpen,  setInputsOpen]  = useState(true);
  const [loading,     setLoading]     = useState(true);   // initial full-page load
  const [resetting,   setResetting]   = useState(false);  // "Reset to PULSE" in progress
  const [saving,      setSaving]      = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [toast,       setToast]       = useState(null);

  // Data-source tracking: 'pulse' | 'saved' | 'fallback'
  const [dataSource,  setDataSource]  = useState('pulse');

  // Working inputs (start with fallbacks; overwritten after init)
  const [demand,         setDemand]         = useState(FALLBACK_DEMAND);
  const [blendedLPH,     setBlendedLPH]     = useState(51.5);
  const [productiveHours,setProductiveHours] = useState(7.5);
  const [calloutBuffer,  setCalloutBuffer]  = useState(10);
  const [schedule,       setSchedule]       = useState(FALLBACK_SCHEDULE);
  const [recvReq,        setRecvReq]        = useState(FALLBACK_RECV_REQ);
  const [transport,      setTransport]      = useState(FALLBACK_TRANSPORT);
  const [catLPH,         setCatLPH]         = useState(FALLBACK_CAT_LPH);

  // PULSE reference data
  const [pulseDefaults,  setPulseDefaults]  = useState(null);
  const [associateLPH,   setAssociateLPH]   = useState([]);
  const [todayProgress,  setTodayProgress]  = useState(null);

  // ── Apply helpers ──────────────────────────────────────────────────────────

  const applyPulse = (pulse) => {
    if (!pulse) return;
    if (pulse.demand && Object.keys(pulse.demand).length > 0) setDemand(pulse.demand);
    if (pulse.blendedLph)                                      setBlendedLPH(pulse.blendedLph);
    if (pulse.catLPH && pulse.catLPH.length > 0)              setCatLPH(pulse.catLPH);
    if (pulse.associates)                                      setAssociateLPH(pulse.associates);
  };

  const applyConfig = (cfg) => {
    if (!cfg) return;
    if (cfg.demand)          setDemand(cfg.demand);
    if (cfg.blendedLPH)      setBlendedLPH(cfg.blendedLPH);
    if (cfg.productiveHours) setProductiveHours(cfg.productiveHours);
    if (cfg.calloutBuffer)   setCalloutBuffer(cfg.calloutBuffer);
    if (cfg.schedule)        setSchedule(cfg.schedule);
    if (cfg.recvReq)         setRecvReq(cfg.recvReq);
    if (cfg.transport)       setTransport(cfg.transport);
    if (cfg.catLPH)          setCatLPH(cfg.catLPH);
  };

  // ── Today progress ─────────────────────────────────────────────────────────

  const fetchTodayProgress = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/labor-planner/today-progress`, { headers: getAuthHeaders() });
      if (res.ok) setTodayProgress(await res.json());
    } catch { /* silent */ }
  }, []);

  // ── Initialization — PULSE + config in parallel ───────────────────────────
  //   Priority: saved config > PULSE > hardcoded fallbacks

  const initialize = useCallback(async () => {
    setLoading(true);

    const [pulseSettled, configSettled] = await Promise.allSettled([
      fetchPulseDataInternal(),
      fetchSavedConfig(),
    ]);

    const pulse  = pulseSettled.status  === 'fulfilled' ? pulseSettled.value  : null;
    const config = configSettled.status === 'fulfilled' ? configSettled.value : null;

    // Always store PULSE reference (for the Reset button and InfoTips)
    setPulseDefaults(pulse);
    if (pulse?.associates) setAssociateLPH(pulse.associates);

    if (config) {
      // Priority 1: user has saved overrides
      applyConfig(config);
      setDataSource('saved');
    } else if (pulse) {
      // Priority 2: PULSE data
      applyPulse(pulse);
      setDataSource('pulse');
    } else {
      // Priority 3: hardcoded fallbacks (already in useState)
      setDataSource('fallback');
    }

    setLoading(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    initialize();
    fetchTodayProgress();
  }, [initialize, fetchTodayProgress]);

  // Auto-refresh today's progress every 60 s when viewing today
  useEffect(() => {
    if (selectedDay !== todayDow) return;
    const id = setInterval(fetchTodayProgress, 60_000);
    return () => clearInterval(id);
  }, [selectedDay, todayDow, fetchTodayProgress]);

  // ── Save (with confirmation dialog) ───────────────────────────────────────

  const handleSaveClick = () => setSaveDialogOpen(true);

  const handleSaveConfirm = async () => {
    setSaveDialogOpen(false);
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/labor-planner/config`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ demand, blendedLPH, productiveHours, calloutBuffer, schedule, recvReq, transport, catLPH })
      });
      if (res.ok) {
        setDataSource('saved');
        setToast({ severity: 'success', message: 'Manual overrides saved' });
      } else {
        setToast({ severity: 'error', message: 'Save failed' });
      }
    } catch {
      setToast({ severity: 'error', message: 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  // ── Reset to PULSE — re-fetch fresh data, clear saved overrides ────────────

  const resetToPulse = async () => {
    setResetting(true);
    try {
      // Fetch fresh PULSE data
      const pulse = await fetchPulseDataInternal();

      if (!pulse) {
        setToast({ severity: 'error', message: 'Unable to reach PULSE endpoints — overrides unchanged' });
        return;
      }

      // Clear saved overrides in DB
      await fetch(`${API_BASE}/api/labor-planner/config`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      }).catch(() => {}); // non-fatal if DELETE not available

      // Apply fresh PULSE data
      setPulseDefaults(pulse);
      applyPulse(pulse);
      setDataSource('pulse');
      setToast({ severity: 'success', message: 'Reset to latest PULSE data — overrides cleared' });
    } catch (err) {
      console.error('Reset failed:', err);
      setToast({ severity: 'error', message: 'Reset failed' });
    } finally {
      setResetting(false);
    }
  };

  // ── Derived values ─────────────────────────────────────────────────────────

  const required     = calcRequired({ dayIndex: selectedDay, demand, blendedLPH, productiveHours, calloutBuffer, recvReq, transport });
  const scheduledDay = schedule[selectedDay] || {};
  const score        = healthScore(scheduledDay, required);

  const totalScheduled = ROLES.reduce((s, r) => s + (Number(scheduledDay[r]) || 0), 0);
  const totalRequired  = ROLES.reduce((s, r) => s + (Number(required[r])  || 0), 0);
  const surplus        = totalScheduled - totalRequired;

  const weeklyRows = DAYS.map((_, di) => {
    const req    = calcRequired({ dayIndex: di, demand, blendedLPH, productiveHours, calloutBuffer, recvReq, transport });
    const sch    = schedule[di] || {};
    const totSch = ROLES.reduce((s, r) => s + (Number(sch[r]) || 0), 0);
    const totReq = ROLES.reduce((s, r) => s + (Number(req[r]) || 0), 0);
    return { day: di, totSch, totReq, diff: totSch - totReq };
  });

  const viewingToday = selectedDay === todayDow;

  // ── Input helpers ──────────────────────────────────────────────────────────

  const setDemandField     = (di, shift, val) => setDemand(p => ({ ...p, [di]: { ...p[di], [shift]: val } }));
  const setScheduleField   = (di, role, val)  => setSchedule(p => ({ ...p, [di]: { ...p[di], [role]: val } }));
  const setRecvReqField    = (di, val)        => setRecvReq(p => ({ ...p, [di]: val }));
  const setTransportField  = (i, field, val)  => setTransport(p => p.map((t, idx) => idx === i ? { ...t, [field]: val } : t));
  const setCatLPHField     = (i, val)         => setCatLPH(p => p.map((c, idx) => idx === i ? { ...c, lph: val } : c));

  const toggleTransportDay = (i, day) =>
    setTransport(p => p.map((t, idx) => {
      if (idx !== i) return t;
      const days = t.days.includes(day) ? t.days.filter(d => d !== day) : [...t.days, day].sort();
      return { ...t, days };
    }));

  // ── Loading spinner ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 2 }}>
        <CircularProgress size={48} />
        <Typography color="text.secondary">Loading PULSE data…</Typography>
      </Box>
    );
  }

  // ── Style tokens ───────────────────────────────────────────────────────────

  const tcell = { py: 0.75, px: 1.5, fontSize: '0.82rem' };
  const thead = { ...tcell, fontWeight: 700, bgcolor: '#f5f5f5', color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.72rem', letterSpacing: '0.05em' };

  const dataRange    = pulseDefaults?.dataRange;
  const demandInfo   = dataRange
    ? `Source: PULSE API — avg from ${dataRange.weeksOfData} weeks of data (${fmtDate(dataRange.from)} – ${fmtDate(dataRange.to)}, ${dataRange.totalOBDs?.toLocaleString()} OBDs)`
    : 'Source: Hardcoded fallbacks — PULSE data unavailable';
  const lphInfo = pulseDefaults?.blendedLph
    ? `Source: PULSE API — ${pulseDefaults.blendedLph} L/Hr calculated from ${fmtDate(pulseDefaults.assocPeriod?.from)} – ${fmtDate(pulseDefaults.assocPeriod?.to)}`
    : 'Source: PULSE Reports — building average Lines/Hr';

  const bannerProps = {
    pulse:    { bgcolor: '#e8f5e9', borderColor: '#c8e6c9', textColor: '#1b5e20',
                text: `✓ PULSE data loaded — ${dataRange?.weeksOfData ?? '?'} weeks (${fmtDate(dataRange?.from)} – ${fmtDate(dataRange?.to)}, ${dataRange?.totalOBDs?.toLocaleString() ?? '?'} OBDs)` },
    saved:    { bgcolor: '#e3f2fd', borderColor: '#bbdefb', textColor: '#0d47a1',
                text: '📌 Using saved manual overrides — click "Reset to PULSE Data" to revert to live values' },
    fallback: { bgcolor: '#fff8e1', borderColor: '#fff176', textColor: '#e65100',
                text: '⚠ Unable to load PULSE data — using hardcoded defaults' },
  }[dataSource];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ bgcolor: '#f0f2f5', minHeight: '100vh' }}>

      {/* ── Header ── */}
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e0e0', px: 3, py: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6" fontWeight={700} sx={{ mr: 1 }}>Labor Planner</Typography>

          {/* Week navigator */}
          <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #e0e0e0', borderRadius: 1 }}>
            <IconButton size="small" onClick={() => setWeekStart(w => addWeeks(w, -1))}>
              <NavigateBeforeIcon fontSize="small" />
            </IconButton>
            <Typography variant="body2" sx={{ px: 1.5, fontWeight: 600, whiteSpace: 'nowrap' }}>
              Week of {format(weekStart, 'MMM d, yyyy')}
            </Typography>
            <IconButton size="small" onClick={() => setWeekStart(w => addWeeks(w, 1))}>
              <NavigateNextIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Day pills */}
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {DAYS.map((d, i) => (
              <Chip key={d} label={d} size="small" onClick={() => setSelectedDay(i)}
                sx={{
                  cursor: 'pointer',
                  fontWeight: selectedDay === i ? 700 : 400,
                  bgcolor: selectedDay === i ? 'primary.main' : 'transparent',
                  color:   selectedDay === i ? 'white' : 'text.primary',
                  border:  selectedDay === i ? 'none' : '1px solid #e0e0e0',
                  '&:hover': { bgcolor: selectedDay === i ? 'primary.dark' : '#f5f5f5' }
                }}
              />
            ))}
          </Box>

          <Box sx={{ flexGrow: 1 }} />
          <Button variant="outlined" size="small" startIcon={<TuneIcon />} onClick={() => setInputsOpen(o => !o)}>
            {inputsOpen ? 'Hide Inputs' : 'Show Inputs'}
          </Button>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ display: 'flex', height: 'calc(100vh - 124px)', overflow: 'hidden' }}>

        {/* ── Main content ── */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 2.5, minWidth: 0 }}>

          {/* Today's Progress */}
          {viewingToday && todayProgress && (
            <Paper sx={{ mb: 2, p: 2, borderLeft: '4px solid #1976d2' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
                  Today's Progress
                  <InfoTip text="Source: PULSE API — real-time OBD completion data. Auto-refreshes every 60 seconds." />
                </Typography>
                <Chip label={`${todayProgress.activeWorkers} active`} size="small" color="primary" variant="outlined" sx={{ mr: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  {todayProgress.completedLines.toLocaleString()} / {todayProgress.totalLines.toLocaleString()} lines
                  ({todayProgress.percentComplete}%)
                </Typography>
                <IconButton size="small" sx={{ ml: 1 }} onClick={fetchTodayProgress}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </Box>
              <LinearProgress
                variant="determinate"
                value={todayProgress.percentComplete}
                sx={{
                  height: 10, borderRadius: 5, bgcolor: '#e3f2fd',
                  '& .MuiLinearProgress-bar': {
                    bgcolor: todayProgress.percentComplete >= 80 ? '#2e7d32' :
                             todayProgress.percentComplete >= 50 ? '#1976d2' : '#f57c00'
                  }
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography variant="caption" color="text.secondary">{todayProgress.totalOBDs} OBDs total</Typography>
                <Typography variant="caption" color="text.secondary">
                  {todayProgress.totalLines > 0
                    ? `${(todayProgress.totalLines - todayProgress.completedLines).toLocaleString()} lines remaining`
                    : 'No OBDs imported yet'}
                </Typography>
              </Box>
            </Paper>
          )}

          {/* KPI Cards */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {[
              { label: 'Total Scheduled', value: totalScheduled, color: '#1976d2' },
              { label: 'Total Required',  value: totalRequired,  color: '#6a1b9a' },
              { label: surplus >= 0 ? 'Surplus' : 'Deficit', value: surplus >= 0 ? `+${surplus}` : String(surplus), color: surplus >= 0 ? '#2e7d32' : '#c62828' },
              { label: 'Shift Health', value: `${score}%`, color: scoreColor(score) },
            ].map(({ label, value, color }) => (
              <Grid item xs={6} sm={3} key={label}>
                <Paper sx={{ p: 2, textAlign: 'center', borderTop: `3px solid ${color}` }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {label}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} sx={{ color, mt: 0.5 }}>{value}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* Shift Health Table */}
          <Paper sx={{ mb: 2 }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight={700}>
                {DAY_FULL[selectedDay]} — Shift Health
              </Typography>
              <Box sx={{ ml: 2, px: 1.5, py: 0.25, borderRadius: 1, bgcolor: scoreColor(score), color: 'white', fontSize: '0.75rem', fontWeight: 700 }}>
                {score >= 80 ? 'HEALTHY' : score >= 60 ? 'AT RISK' : 'UNDERSTAFFED'}
              </Box>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Role', 'Scheduled', 'Required', 'Variance', 'Status'].map(h => (
                      <TableCell key={h} sx={thead}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ROLES.map(role => {
                    const sch = Number(scheduledDay[role]) || 0;
                    const req = Number(required[role]) || 0;
                    const v   = sch - req;
                    const { label, bgcolor, color } = variantChip(v);
                    return (
                      <TableRow key={role} hover>
                        <TableCell sx={tcell}>{role}</TableCell>
                        <TableCell sx={{ ...tcell, fontWeight: 600 }}>{sch}</TableCell>
                        <TableCell sx={tcell}>{req}</TableCell>
                        <TableCell sx={tcell}>
                          <Box component="span" sx={{ px: 0.75, py: 0.25, borderRadius: 0.5, bgcolor, color, fontWeight: 700, fontSize: '0.78rem' }}>
                            {label}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...tcell, color: v > 0 ? '#1565c0' : v === 0 ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
                          {v > 0 ? 'Over' : v === 0 ? 'Met' : 'Under'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Weekly Overview */}
          <Paper>
            <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography variant="subtitle1" fontWeight={700}>Weekly Overview</Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Day', 'AM Lines', 'PM Lines', 'Total Lines', 'Scheduled HC', 'Required HC', 'Variance', 'Status'].map(h => (
                      <TableCell key={h} sx={thead}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {weeklyRows.map(({ day, totSch, totReq, diff }) => {
                    const amL = Number(demand[day]?.am) || 0;
                    const pmL = Number(demand[day]?.pm) || 0;
                    const { label, bgcolor, color } = variantChip(diff);
                    return (
                      <TableRow key={day} hover selected={day === selectedDay}
                        onClick={() => setSelectedDay(day)}
                        sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: '#e3f2fd' } }}>
                        <TableCell sx={{ ...tcell, fontWeight: day === selectedDay ? 700 : 400 }}>
                          {DAYS[day]}
                          {day === todayDow && (
                            <Chip label="Today" size="small" sx={{ ml: 1, height: 16, fontSize: '0.65rem' }} color="primary" />
                          )}
                        </TableCell>
                        <TableCell sx={tcell}>{amL.toLocaleString()}</TableCell>
                        <TableCell sx={tcell}>{pmL.toLocaleString()}</TableCell>
                        <TableCell sx={{ ...tcell, fontWeight: 600 }}>{(amL + pmL).toLocaleString()}</TableCell>
                        <TableCell sx={tcell}>{totSch}</TableCell>
                        <TableCell sx={tcell}>{totReq}</TableCell>
                        <TableCell sx={tcell}>
                          <Box component="span" sx={{ px: 0.75, py: 0.25, borderRadius: 0.5, bgcolor, color, fontWeight: 700, fontSize: '0.78rem' }}>
                            {label}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ ...tcell, color: diff > 0 ? '#1565c0' : diff === 0 ? '#2e7d32' : '#c62828', fontWeight: 600 }}>
                          {diff > 0 ? 'Over' : diff === 0 ? 'OK' : 'Under'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>

        {/* ── Inputs Panel ── */}
        <Box sx={{
          width: inputsOpen ? 410 : 0,
          minWidth: inputsOpen ? 410 : 0,
          overflow: 'hidden',
          transition: 'width 0.25s ease, min-width 0.25s ease',
          borderLeft: '1px solid #e0e0e0',
          bgcolor: 'white',
        }}>
          <Box sx={{ width: 410, overflow: 'auto', height: '100%', pb: 4 }}>

            {/* Panel header */}
            <Box sx={{
              px: 2, py: 1.5, borderBottom: '1px solid #f0f0f0',
              display: 'flex', alignItems: 'center', gap: 1,
              position: 'sticky', top: 0, bgcolor: 'white', zIndex: 1
            }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>
                Input Parameters
              </Typography>
              <Button
                size="small" variant="outlined" color="info"
                startIcon={resetting ? <CircularProgress size={12} color="inherit" /> : <RefreshIcon />}
                onClick={resetToPulse}
                disabled={resetting}
                sx={{ fontSize: '0.7rem', py: 0.25 }}
              >
                Reset to PULSE Data
              </Button>
              <Button
                variant="contained" size="small"
                startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
                onClick={handleSaveClick}
                disabled={saving}
              >
                Save
              </Button>
            </Box>

            {/* Data-source banner */}
            {bannerProps && (
              <Box sx={{ px: 2, py: 0.75, bgcolor: bannerProps.bgcolor, borderBottom: `1px solid ${bannerProps.borderColor}` }}>
                <Typography variant="caption" sx={{ color: bannerProps.textColor }}>{bannerProps.text}</Typography>
              </Box>
            )}

            {/* ── Demand Lines ── */}
            <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid #f0f0f0' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography variant="body2" fontWeight={700}>
                  Demand Lines by Shift
                  <InfoTip text={demandInfo} />
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thead}>Day</TableCell>
                      <TableCell sx={thead}>AM Lines</TableCell>
                      <TableCell sx={thead}>PM Lines</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {DAYS.map((d, di) => (
                      <TableRow key={d}>
                        <TableCell sx={{ ...tcell, fontWeight: selectedDay === di ? 700 : 400 }}>{d}</TableCell>
                        <TableCell sx={tcell}>
                          <TextField type="number" size="small" variant="standard"
                            value={demand[di]?.am ?? ''}
                            onChange={e => setDemandField(di, 'am', Number(e.target.value))}
                            inputProps={{ style: { textAlign: 'right', fontSize: '0.82rem' }, min: 0 }}
                            sx={{ width: 70 }} />
                        </TableCell>
                        <TableCell sx={tcell}>
                          <TextField type="number" size="small" variant="standard"
                            value={demand[di]?.pm ?? ''}
                            onChange={e => setDemandField(di, 'pm', Number(e.target.value))}
                            inputProps={{ style: { textAlign: 'right', fontSize: '0.82rem' }, min: 0 }}
                            sx={{ width: 70 }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>

            {/* ── Productivity Parameters ── */}
            <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid #f0f0f0' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography variant="body2" fontWeight={700}>Productivity Parameters</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {[
                    { label: 'Blended Lines/Hr',    tip: lphInfo,                                                                 value: blendedLPH,      setter: setBlendedLPH,      step: 0.5 },
                    { label: 'Productive Hrs/Shift', tip: 'Source: Manual Input — net productive hours after breaks',             value: productiveHours, setter: setProductiveHours, step: 0.5 },
                    { label: 'Call-out Buffer (%)',  tip: 'Source: Manual Input — historical call-out rate',                      value: calloutBuffer,   setter: setCalloutBuffer,   step: 1   },
                  ].map(({ label, tip, value, setter, step }) => (
                    <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{label}<InfoTip text={tip} /></Typography>
                      <TextField type="number" size="small" variant="outlined"
                        value={value} onChange={e => setter(e.target.value)}
                        inputProps={{ style: { textAlign: 'right', width: 60 }, step }}
                        sx={{ width: 90 }} />
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* ── Schedule Headcounts ── */}
            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid #f0f0f0' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography variant="body2" fontWeight={700}>
                  Schedule Headcounts
                  <InfoTip text="Source: Manual Input — from weekly schedule PDF" />
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5, overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 340 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...thead, minWidth: 110 }}>Role</TableCell>
                      {DAYS.map(d => <TableCell key={d} sx={{ ...thead, textAlign: 'center', px: 0.5 }}>{d}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ROLES.map(role => (
                      <TableRow key={role}>
                        <TableCell sx={{ ...tcell, fontSize: '0.75rem' }}>{role}</TableCell>
                        {DAYS.map((_, di) => (
                          <TableCell key={di} sx={{ px: 0.5, py: 0.25 }}>
                            <TextField type="number" size="small" variant="standard"
                              value={schedule[di]?.[role] ?? ''}
                              onChange={e => setScheduleField(di, role, Number(e.target.value))}
                              inputProps={{ style: { textAlign: 'center', fontSize: '0.78rem', width: 28 }, min: 0 }}
                              sx={{ width: 36 }} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>

            {/* ── Receiving Requirements ── */}
            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid #f0f0f0' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography variant="body2" fontWeight={700}>
                  Receiving Requirements
                  <InfoTip text="Source: Manual Input — vendor delivery schedule" />
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thead}>Day</TableCell>
                      <TableCell sx={thead}>Receivers Needed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {DAYS.map((d, di) => (
                      <TableRow key={d}>
                        <TableCell sx={tcell}>{d}</TableCell>
                        <TableCell sx={tcell}>
                          <TextField type="number" size="small" variant="standard"
                            value={recvReq[di] ?? ''}
                            onChange={e => setRecvReqField(di, Number(e.target.value))}
                            inputProps={{ style: { fontSize: '0.82rem' }, min: 0 }}
                            sx={{ width: 70 }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>

            {/* ── Transport Schedule ── */}
            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid #f0f0f0' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography variant="body2" fontWeight={700}>
                  Transport Schedule
                  <InfoTip text="Source: Manual Input — carrier schedule" />
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
                {transport.map((t, i) => (
                  <Paper key={t.platform} variant="outlined" sx={{ mb: 1, p: 1 }}>
                    <Typography variant="caption" fontWeight={700} color="primary">{t.platform}</Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
                      {[
                        { label: 'Shift', field: 'shift', width: 70 },
                        { label: 'Dispatch', field: 'dispatch', width: 85 },
                        { label: 'Carrier', field: 'carrier', width: undefined },
                      ].map(({ label, field, width }) => (
                        <TextField key={field} label={label} size="small" value={t[field]}
                          onChange={e => setTransportField(i, field, e.target.value)}
                          sx={{ width: width || undefined, flex: width ? undefined : 1, minWidth: width ? undefined : 100 }}
                          inputProps={{ style: { fontSize: '0.78rem' } }} />
                      ))}
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {DAYS.map((d, di) => (
                        <Chip key={d} label={d} size="small" onClick={() => toggleTransportDay(i, di)}
                          sx={{
                            fontSize: '0.7rem', cursor: 'pointer',
                            bgcolor: t.days.includes(di) ? 'primary.main' : 'transparent',
                            color:   t.days.includes(di) ? 'white' : 'text.secondary',
                            border:  t.days.includes(di) ? 'none' : '1px solid #e0e0e0',
                          }} />
                      ))}
                    </Box>
                  </Paper>
                ))}
              </AccordionDetails>
            </Accordion>

            {/* ── Category LPH ── */}
            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid #f0f0f0' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography variant="body2" fontWeight={700}>
                  Category LPH
                  <InfoTip text={
                    pulseDefaults?.catPeriod
                      ? `Source: PULSE API — Lines/Hr by category (${fmtDate(pulseDefaults.catPeriod.from)} – ${fmtDate(pulseDefaults.catPeriod.to)})`
                      : 'Source: PULSE Reports — Lines/Hr by category'
                  } />
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={thead}>Category</TableCell>
                      <TableCell sx={thead}>Lines/Hr</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {catLPH.map((c, i) => (
                      <TableRow key={c.category}>
                        <TableCell sx={tcell}>{c.category}</TableCell>
                        <TableCell sx={tcell}>
                          <TextField type="number" size="small" variant="standard"
                            value={c.lph}
                            onChange={e => setCatLPHField(i, Number(e.target.value))}
                            inputProps={{ style: { fontSize: '0.82rem' }, min: 0 }}
                            sx={{ width: 70 }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>

            {/* ── Associate Pick Rates ── */}
            <Accordion disableGutters elevation={0} sx={{ '&:before': { display: 'none' }, borderBottom: '1px solid #f0f0f0' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, minHeight: 40, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
                <Typography variant="body2" fontWeight={700}>
                  Associate Pick Rates
                  <InfoTip text={
                    pulseDefaults?.assocPeriod
                      ? `Source: PULSE API — individual L/Hr from ${fmtDate(pulseDefaults.assocPeriod.from)} – ${fmtDate(pulseDefaults.assocPeriod.to)} (min 5 OBDs)`
                      : 'Source: PULSE API — individual Lines/Hr from last 28 days'
                  } />
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 2, pt: 0, pb: 1.5 }}>
                {associateLPH.length === 0 ? (
                  <Typography variant="caption" color="text.secondary">
                    No associate data available for last 28 days.
                  </Typography>
                ) : (
                  <>
                    {pulseDefaults?.blendedLph && (
                      <Box sx={{ mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Team blended: <strong>{pulseDefaults.blendedLph} L/Hr</strong>
                          {' '}({associateLPH.length} associates with ≥5 OBDs)
                        </Typography>
                      </Box>
                    )}
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          {['Associate', 'L/Hr', 'OBDs', 'Hours'].map(h => <TableCell key={h} sx={thead}>{h}</TableCell>)}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {associateLPH.map(a => (
                          <TableRow key={a.name}>
                            <TableCell sx={{ ...tcell, fontSize: '0.78rem' }}>{a.name}</TableCell>
                            <TableCell sx={{ ...tcell, fontWeight: 600, color: a.lph >= (pulseDefaults?.blendedLph || 51.5) ? '#2e7d32' : '#c62828' }}>
                              {a.lph}
                            </TableCell>
                            <TableCell sx={tcell}>{a.obdsCompleted}</TableCell>
                            <TableCell sx={tcell}>{a.hoursWorked}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Box>

      {/* ── Save Confirmation Dialog ── */}
      <Dialog open={saveDialogOpen} onClose={() => setSaveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="warning" />
          Save Manual Overrides?
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            You are saving manual overrides to PULSE data.
          </Alert>
          <DialogContentText>
            The current values will be stored and used instead of live PULSE averages every time this page loads —
            until you click <strong>"Reset to PULSE Data"</strong>. Any changes to demand lines, blended LPH,
            and category LPH will override the automatically calculated values.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveConfirm} variant="contained" color="warning" startIcon={<SaveIcon />}>
            Save Overrides
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Toast ── */}
      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={toast?.severity} onClose={() => setToast(null)} variant="filled">
          {toast?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
